/**
 * useCompetitionSessions — running a session on the day, not defining one.
 *
 * The timer machine (scheduled -> running <-> paused -> finished) and the
 * finals phase machine (null -> observation -> climbing <-> rest -> finished).
 * Creating and deleting session documents is structure, and lives in
 * `useCompetitionAdmin` with the rest of the editor's writes.
 *
 * `writeSession` is exported alongside them because one caller outside this
 * module needs it — `setSessionAttemptsLocked`, which stays with its
 * competition-level twin in the admin hook rather than being separated from it.
 */
import { useCallback } from "react";
import { doc, updateDoc, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { findBlockingSession } from "../../competitionUtils";

export function useCompetitionSessions({ gymId, sessions, withActor }) {
  // States: scheduled → running ↔ paused → finished
  // Transitions: Start, Pause, Resume, Finish, Restart

  /**
   * Session-doc write with the actor stamp. Eleven call sites below (the five
   * timer transitions, the four finals phases, the timer adjustment and the
   * per-session attempts lock) used to repeat the same six-segment path and
   * would each have needed their own `withActor` wrap.
   */
  const writeSession = useCallback((compId, sessionId, data) => updateDoc(
    doc(db, "gyms", gymId, "competitions", compId, "sessions", sessionId),
    withActor(data),
  ), [gymId, withActor]);

  const startSession = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    // Safety-net guard: only one session per competition may be running or
    // paused at a time. `sessions` is scoped to activeCompId by the detail
    // listener above, so no competition-id filter is needed here.
    if (findBlockingSession(sessions, sessionId)) return;
    const sessionDoc = sessions.find(s => s.id === sessionId);
    const dur = sessionDoc?.durationMinutes || 0;
    const now = new Date();
    const endTime = dur > 0 ? new Date(now.getTime() + dur * 60000) : null;
    await writeSession(compId, sessionId, {
      status: "running",
      actualStartTime: Timestamp.fromDate(now),
      endTime: endTime ? Timestamp.fromDate(endTime) : null,
      remainingSeconds: null,
      pausedAt: null,
    });
  }, [gymId, sessions]);

  const pauseSession = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    const sessionDoc = sessions.find(s => s.id === sessionId);
    if (!sessionDoc || sessionDoc.status !== "running") return;
    // Compute remaining seconds from endTime
    let remainingSecs = 0;
    const et = sessionDoc.endTime;
    if (et) {
      const endMs = et.toDate ? et.toDate().getTime() : et.seconds ? et.seconds * 1000 : new Date(et).getTime();
      remainingSecs = Math.max(0, Math.floor((endMs - Date.now()) / 1000));
    }
    await writeSession(compId, sessionId, {
      status: "paused",
      remainingSeconds: remainingSecs,
      pausedAt: serverTimestamp(),
    });
  }, [gymId, sessions]);

  const resumeSession = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    const sessionDoc = sessions.find(s => s.id === sessionId);
    if (!sessionDoc || sessionDoc.status !== "paused") return;
    const remaining = sessionDoc.remainingSeconds || 0;
    if (remaining <= 0) return;
    const now = new Date();
    const endTime = new Date(now.getTime() + remaining * 1000);
    await writeSession(compId, sessionId, {
      status: "running",
      endTime: Timestamp.fromDate(endTime),
      remainingSeconds: null,
      pausedAt: null,
    });
  }, [gymId, sessions]);

  const finishSession = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    await writeSession(compId, sessionId, {
      status: "finished",
      actualEndTime: serverTimestamp(),
      remainingSeconds: null,
      pausedAt: null,
    });
  }, [gymId]);

  // Adjust remaining seconds on a paused session — lets the organizer extend
  // or shorten the remaining time before resuming. Only valid in "paused" state.
  const adjustRemainingSeconds = useCallback(async (compId, sessionId, seconds) => {
    if (!gymId) return;
    const sessionDoc = sessions.find(s => s.id === sessionId);
    if (!sessionDoc || sessionDoc.status !== "paused") return;
    const safe = Math.max(0, Math.floor(Number(seconds) || 0));
    await writeSession(compId, sessionId, {
      remainingSeconds: safe,
    });
  }, [gymId, sessions]);

  const restartSession = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    // Same "one session at a time" safety net as startSession.
    if (findBlockingSession(sessions, sessionId)) return;
    const sessionDoc = sessions.find(s => s.id === sessionId);
    const dur = sessionDoc?.durationMinutes || 0;
    const now = new Date();
    const endTime = dur > 0 ? new Date(now.getTime() + dur * 60000) : null;
    await writeSession(compId, sessionId, {
      status: "running",
      actualStartTime: Timestamp.fromDate(now),
      endTime: endTime ? Timestamp.fromDate(endTime) : null,
      actualEndTime: null,
      remainingSeconds: null,
      pausedAt: null,
    });
  }, [gymId, sessions]);

  // ── Finals session phase control ──
  // Phase machine: null → observation → climbing ↔ rest → finished

  const startFinalsObservation = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    const sessionDoc = sessions.find(s => s.id === sessionId);
    if (!sessionDoc?.finals) return;
    const obsTime = sessionDoc.finals.observationTime || 120;
    const now = new Date();
    const endTime = new Date(now.getTime() + obsTime * 1000);
    await writeSession(compId, sessionId, {
      status: "running",
      actualStartTime: Timestamp.fromDate(now),
      endTime: Timestamp.fromDate(endTime),
      "finals.phase": "observation",
      "finals.currentRound": null,
    });
  }, [gymId, sessions]);

  const startFinalsClimbing = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    const sessionDoc = sessions.find(s => s.id === sessionId);
    if (!sessionDoc?.finals) return;
    const timePerBoulder = sessionDoc.finals.timePerBoulder || 240;
    const now = new Date();
    const endTime = new Date(now.getTime() + timePerBoulder * 1000);
    const currentRound = (sessionDoc.finals.currentRound || 0) + 1;
    await writeSession(compId, sessionId, {
      status: "running",
      endTime: Timestamp.fromDate(endTime),
      remainingSeconds: null,
      pausedAt: null,
      "finals.phase": "climbing",
      "finals.currentRound": currentRound,
    });
  }, [gymId, sessions]);

  const startFinalsRest = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    const sessionDoc = sessions.find(s => s.id === sessionId);
    if (!sessionDoc?.finals) return;
    const restTime = sessionDoc.finals.restBetweenRounds || 60;
    const now = new Date();
    const endTime = new Date(now.getTime() + restTime * 1000);
    await writeSession(compId, sessionId, {
      status: "running",
      endTime: Timestamp.fromDate(endTime),
      remainingSeconds: null,
      pausedAt: null,
      "finals.phase": "rest",
    });
  }, [gymId, sessions]);

  const finishFinals = useCallback(async (compId, sessionId) => {
    if (!gymId) return;
    await writeSession(compId, sessionId, {
      status: "finished",
      actualEndTime: serverTimestamp(),
      remainingSeconds: null,
      pausedAt: null,
      "finals.phase": "finished",
    });
  }, [gymId]);

  return {
    writeSession,
    startSession, pauseSession, resumeSession, finishSession, restartSession, adjustRemainingSeconds,
    startFinalsObservation, startFinalsClimbing, startFinalsRest, finishFinals,
  };
}
